import type { SvgIconProps } from "@mui/material/SvgIcon";
import SvgIcon from "@mui/material/SvgIcon";
import type * as I from "core/api/coreApiClient";
import type { ApplicationState } from "core/redux/reducers/rootReducer";
import theme from "core/utilities/theme";
import React from "react";
import { useSelector } from "react-redux";

interface CommonIconComponentProps {
  mr?: boolean;
  ml?: boolean;
  color?: SvgIconProps["color"];
  align?: "top" | "bottom" | "middle";
  pullRight?: boolean;
  rotate?: number;
  style?: React.CSSProperties;
  loop?: boolean;
}

export interface ComponentProps extends CommonIconComponentProps {
  element: typeof SvgIcon;
  path?: string;
}

function Icon({ ml, mr, color, element, path, align, pullRight, rotate, style, loop }: ComponentProps) {
  return React.createElement(
    element,
    {
      fontSize: "small",
      sx: {
        verticalAlign: align || "top",
        margin: theme.spacing(0, mr ? 0.5 : 0, 0, ml ? 0.5 : 0),
        ...(pullRight ? { position: "absolute", right: "6px" } : {}),
        ...(rotate ? { transform: `rotate(${rotate}deg)` } : {}),
        ...(loop
          ? {
              animation: "spin 2s linear infinite",
              "@keyframes spin": {
                "0%": {
                  transform: "rotate(360deg)",
                },
                "100%": {
                  transform: "rotate(0deg)",
                },
              },
            }
          : {}),
        ...(style || {}),
      },
      color,
    },
    path ? <path d={path} /> : undefined
  );
}

export function DownloadIcon(props: CommonIconComponentProps) {
  return <Icon {...props} element={SvgIcon} path="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" align="bottom" />;
}

export function CalculatorIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M7,2H17A2,2 0 0,1 19,4V20A2,2 0 0,1 17,22H7A2,2 0 0,1 5,20V4A2,2 0 0,1 7,2M7,4V8H17V4H7M7,10V12H9V10H7M11,10V12H13V10H11M15,10V12H17V10H15M7,14V16H9V14H7M11,14V16H13V14H11M15,14V16H17V14H15M7,18V20H9V18H7M11,18V20H13V18H11M15,18V20H17V18H15Z"
      align="bottom"
    />
  );
}

export function ResizeIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M18,16V13H15V22H13V2H15V11H18V8L22,12L18,16M2,12L6,16V13H9V22H11V2H9V11H6V8L2,12Z"
      align="bottom"
    />
  );
}

export function MoveColumnLeftIcon(props: CommonIconComponentProps) {
  return <Icon {...props} element={SvgIcon} path="M11,16V13H21V11H11V8L7,12L11,16M3,20H5V4H3V20Z" align="bottom" />;
}

export function MoveColumnRightIcon(props: CommonIconComponentProps) {
  return <Icon {...props} element={SvgIcon} path="M13,8V11H3V13H13V16L17,12L13,8M19,20H21V4H19V20Z" align="bottom" />;
}

export function MoveLineTopIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M13,8V11H3V13H13V16L17,12L13,8M19,20H21V4H19V20Z"
      align="bottom"
      rotate={-90}
    />
  );
}

export function MoveLineBottomIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M13,8V11H3V13H13V16L17,12L13,8M19,20H21V4H19V20Z"
      align="bottom"
      rotate={90}
    />
  );
}

export function MoveLineUpDownIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M18,16V13H15V22H13V2H15V11H18V8L22,12L18,16M2,12L6,16V13H9V22H11V2H9V11H6V8L2,12Z"
      align="bottom"
      rotate={90}
    />
  );
}

export function PinIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12M8.8,14L10,12.8V4H14V12.8L15.2,14H8.8Z"
      align="bottom"
    />
  );
}

export function UnpinIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M8,6.2V4H7V2H17V4H16V12L18,14V16H17.8L14,12.2V4H10V8.2L8,6.2M20,20.7L18.7,22L12.8,16.1V22H11.2V16H6V14L8,12V11.3L2,5.3L3.3,4L20,20.7M8.8,14H10.6L9.7,13.1L8.8,14Z"
      align="bottom"
    />
  );
}

export function SequenceFeaturesIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M3 1C1.89 1 1 1.89 1 3V14C1 15.11 1.89 16 3 16H14C15.11 16 16 15.11 16 14V11H14V14H3V3H14V5H16V3C16 1.89 15.11 1 14 1M9 7C7.89 7 7 7.89 7 9V12H9V9H20V20H9V18H7V20C7 21.11 7.89 22 9 22H20C21.11 22 22 21.11 22 20V9C22 7.89 21.11 7 20 7H9"
      align="bottom"
    />
  );
}

export function MoleculesIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M19,8a3,3,0,1,0-3-3,3,3,0,0,0,.3,1.29L14,8.57a3.88,3.88,0,0,0-4,0L8.7,7.29A3,3,0,0,0,9,6,3,3,0,1,0,6,9a3,3,0,0,0,1.29-.3L8.57,10a3.88,3.88,0,0,0,0,4L6.29,16.3A3,3,0,0,0,5,16a3,3,0,1,0,3,3,3,3,0,0,0-.3-1.29L10,15.43a3.88,3.88,0,0,0,4,0l1.28,1.28A3,3,0,0,0,15,18a3,3,0,1,0,3-3,3,3,0,0,0-1.29.3L15.43,14a3.88,3.88,0,0,0,0-4L17.71,7.7A3,3,0,0,0,19,8ZM6,7A1,1,0,1,1,7,6,1,1,0,0,1,6,7ZM18,17a1,1,0,1,1-1,1A1,1,0,0,1,18,17Z"
      align="bottom"
    />
  );
}

export function ExpandIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M10,21V19H6.41L10.91,14.5L9.5,13.09L5,17.59V14H3V21H10M14.5,10.91L19,6.41V10H21V3H14V5H17.59L13.09,9.5L14.5,10.91Z"
      align="bottom"
    />
  );
}

export function CollapseIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M19.5,3.09L15,7.59V4H13V11H20V9H16.41L20.91,4.5L19.5,3.09M4,13V15H7.59L3.09,19.5L4.5,20.91L9,16.41V20H11V13H4Z"
      align="bottom"
    />
  );
}

export function TimeZoneIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M15 12.5V16.5L18 18.5L18.75 17.25L16.5 15.75V12.5H15M22 12.39C22 12.26 22 12.13 22 12C22 6.5 17.5 2 12 2C6.47 2 2 6.5 2 12C2 17.5 6.5 22 12 22C12.13 22 12.24 22 12.37 21.97C13.43 22.62 14.67 23 16 23C19.86 23 23 19.86 23 16C23 14.68 22.62 13.44 22 12.39M19.76 10.11C19.7 10.07 19.65 10.04 19.59 10H19.74C19.75 10.03 19.75 10.07 19.76 10.11M18.92 8H15.97C15.65 6.75 15.19 5.55 14.59 4.44C16.43 5.07 17.96 6.34 18.92 8M12 4.03C12.83 5.23 13.5 6.57 13.91 8H10.09C10.5 6.57 11.17 5.23 12 4.03M9.66 10H12.41C11.16 10.75 10.15 11.88 9.57 13.24C9.53 12.83 9.5 12.42 9.5 12C9.5 11.32 9.56 10.65 9.66 10M9.4 4.44C8.8 5.55 8.35 6.75 8 8H5.08C6.03 6.34 7.57 5.06 9.4 4.44M4.26 14C4.1 13.36 4 12.69 4 12S4.1 10.64 4.26 10H7.64C7.56 10.66 7.5 11.32 7.5 12S7.56 13.34 7.64 14H4.26M5.08 16H8C8.35 17.25 8.8 18.45 9.4 19.56C7.57 18.93 6.03 17.65 5.08 16M16 21C13.24 21 11 18.76 11 16S13.24 11 16 11 21 13.24 21 16 18.76 21 16 21Z"
      align="bottom"
    />
  );
}

export function PencilOffIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M10,10.2L14,6.2L17.8,10L13.8,14L12.4,12.6L15,9.9L14.1,9L11.5,11.6L10,10.2M20.7,5.6L18.4,3.3C18.2,3.1 17.9,3 17.7,3C17.5,3 17.2,3.1 17,3.3L15.2,5.1L19,8.9L20.7,7C21.1,6.7 21.1,6 20.7,5.6M19,21.7L17.7,23L11.2,16.5L6.8,21H3V17.2L7.5,12.7L1,6.3L2.3,5L19,21.7M9.8,15.1L8.9,14.2L5,18.1V19H5.9L9.8,15.1Z"
      align="bottom"
    />
  );
}

export function NewSelectionIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M 3 10 H 14 V 12 H 3 V 10 M 3 6 H 14 V 8 H 3 V 6 M 3 14 H 10 V 16 H 3 V 14 Z"
      align="bottom"
    />
  );
}

export function ResizeBottomRightIcon(props: CommonIconComponentProps) {
  return (
    <Icon
      {...props}
      element={SvgIcon}
      path="M22,22H20V20H22V22M22,18H20V16H22V18M18,22H16V20H18V22M18,18H16V16H18V18M14,22H12V20H14V22M22,14H20V12H22V14Z"
      align="bottom"
    />
  );
}

// Genedata icons representing different entities, used in section, table of content for example
interface EntityTypeIconProps {
  icon: I.IconRef;
  ml?: boolean;
  mr?: boolean;
  large?: boolean;
  ariaHidden?: boolean;
}

export function EntityTypeIcon({ icon, ml, mr, large, ariaHidden }: EntityTypeIconProps) {
  const jsfUrl = useSelector((state: ApplicationState) => state.configuration.jsfUrl);

  return (
    <img
      style={{
        height: large ? "28px" : "20px",
        verticalAlign: "middle",
        marginLeft: theme.spacing(ml ? 1 : 0),
        marginRight: theme.spacing(mr ? 1 : 0),
      }}
      src={`${jsfUrl}${icon.url}`}
      alt={icon.title}
      aria-hidden={ariaHidden}
    />
  );
}

export default Icon;
